import { StaffSessionUser } from "../models/StaffSessionUser.js";
import { ViewModel } from "../mvvm/ViewModel.js";
/** One staff login as the team page shows it. */
export class StaffAccount {
    id;
    username;
    displayName;
    phoneNumber;
    roles;
    permissions;
    constructor(id, username, displayName, phoneNumber, roles, permissions) {
        this.id = id;
        this.username = username;
        this.displayName = displayName;
        this.phoneNumber = phoneNumber;
        this.roles = roles;
        this.permissions = permissions;
    }
    static fromUser(u) {
        const username = u.email.split("@")[0] || u.email;
        const role = u.role || "sales";
        return new StaffAccount(u.id, username, username, u.phone ?? "", [role], StaffSessionUser.permissionsForRole(role));
    }
}
/** Team page: list, add, change role/phone, deactivate. */
export class StaffAccountsViewModel extends ViewModel {
    client;
    options;
    constructor(client, options = {}) {
        super({ accounts: [], loading: false, error: null });
        this.client = client;
        this.options = options;
    }
    async load() {
        await this.run(async () => {
            const users = await this.client.listStaff();
            this.setState({ accounts: users.filter((u) => u.active !== false).map(StaffAccount.fromUser) });
        }, "Failed to load team.");
    }
    /** The temporary password is returned once as the one-time code. */
    async create(input) {
        const resolve = this.options.resolveEmail ?? ((raw) => raw.trim().toLowerCase());
        const email = resolve(input.username);
        const password = (this.options.tempPassword ?? defaultTempPassword)();
        const phone = input.phoneNumber?.trim();
        const created = await this.run(async () => {
            await this.client.createStaff({
                email,
                password,
                role: input.roles[0] || "sales",
                ...(phone ? { phone } : {}),
            });
            await this.load();
            return { username: email.split("@")[0] || email, resetCode: password };
        }, "Failed to create account");
        return created ?? null;
    }
    async update(id, patch) {
        const ok = await this.run(async () => {
            await this.client.patchStaff(id, {
                role: patch.roles[0] || "sales",
                ...(patch.phoneNumber !== undefined ? { phone: patch.phoneNumber.trim() } : {}),
            });
            await this.load();
            return true;
        }, "Failed to update account");
        return ok === true;
    }
    /** Deactivates the login; the API keeps the row. */
    async remove(id) {
        const ok = await this.run(async () => {
            await this.client.patchStaff(id, { active: false });
            await this.load();
            return true;
        }, "Failed to delete");
        return ok === true;
    }
    /** Issues a one-time temporary password; the person must change it on first sign-in (#1077). */
    async resetPassword(id) {
        const reset = this.client.resetStaffPassword;
        if (!reset) {
            this.setState({ error: "Password reset is not available on this server." });
            return null;
        }
        const res = await this.run(async () => {
            const out = await reset.call(this.client, id);
            return { username: out.user.email.split("@")[0] || out.user.email, resetCode: out.temporaryPassword };
        }, "Failed to reset password");
        return res ?? null;
    }
}
function defaultTempPassword() {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 12);
}
//# sourceMappingURL=StaffAccountsViewModel.js.map